import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft, Buildings, Trash, Archive, Fire, Eye, 
  Warning, CheckCircle, XCircle, Funnel, ArrowsClockwise,
  Clock, Spinner, Check, X
} from "phosphor-react";

// =============================================================================
// TENANT STATUS HELPERS - Single source of truth for failed state detection
// =============================================================================
function getTenantStatusValue(tenant) {
  return tenant?.status ?? tenant?.status_display ?? tenant?.normalized_status ?? tenant?.raw_status ?? "";
}

function isFailedTenant(tenant) {
  return String(getTenantStatusValue(tenant)).toUpperCase().endsWith("_FAILED");
}

function isSelectableForUI(tenant) {
  // A tenant is selectable if:
  // 1. Backend says it's selectable (tenant.selectable), OR
  // 2. It has any action flags (can_delete, can_archive, can_true_purge), OR
  // 3. It's in a FAILED state (admin needs to take corrective action)
  const hasActionFlags = Boolean(tenant?.can_delete || tenant?.can_archive || tenant?.can_true_purge);
  return Boolean(tenant?.selectable) || hasActionFlags || isFailedTenant(tenant);
}

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Request timeout in ms
const REQUEST_TIMEOUT = 30000;
// Polling interval in ms
const POLL_INTERVAL = 2000;
// Max polling duration in ms
const MAX_POLL_DURATION = 120000;

// =============================================================================
// MEMOIZED CONFIRMATION MODAL - Prevents re-renders from parent state changes
// Uses uncontrolled input to avoid setState on every keystroke
// =============================================================================
const ConfirmActionModal = memo(function ConfirmActionModal({
  isOpen,
  onOpenChange,
  actionType, // "delete" | "archive" | "purge"
  actionLabel, // "Delete" | "Archive" | "Purge"
  confirmPhrase, // "DELETE" | "ARCHIVE" | dynamic purge phrase
  tenantNames,
  tenantCount,
  warningMessage,
  warningDetails,
  colorScheme, // { accent, bg, border, button }
  icon: IconComponent,
  onExecute,
  isLoading,
}) {
  // Uncontrolled input - no React state per keystroke
  const inputRef = useRef(null);
  const valueRef = useRef("");
  
  // Only track validity (flips at most twice: false→true, true→false)
  const [isValid, setIsValid] = useState(false);
  
  // Track if modal was previously open to detect open->close->open transitions
  const wasOpenRef = useRef(false);
  
  // Reset when modal opens (use layout effect to run before paint)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened - reset values
      valueRef.current = "";
      // Reset input value on next tick (after render)
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.value = "";
          inputRef.current.focus();
        }
        // Only set isValid after timeout to avoid cascading render
        setIsValid(false);
      }, 0);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);
  
  const handleInputChange = useCallback((e) => {
    const v = e.target.value;
    valueRef.current = v;
    // Only update state if validity actually changes
    const nextValid = v.trim().toUpperCase() === confirmPhrase.toUpperCase();
    setIsValid(prev => prev === nextValid ? prev : nextValid);
  }, [confirmPhrase]);
  
  const handleExecute = useCallback(() => {
    const v = valueRef.current.trim().toUpperCase();
    if (v !== confirmPhrase.toUpperCase()) {
      toast.error(`Please type ${confirmPhrase} to confirm`);
      return;
    }
    onExecute();
  }, [confirmPhrase, onExecute]);
  
  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg max-h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className={`flex items-center gap-2 ${colorScheme.accent}`}>
            <IconComponent size={20} />
            Batch {actionLabel} Tenants
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {actionType === "delete" && `This will delete ${tenantCount} tenant(s) and create exports for each. Deleted tenants can be restored or archived later.`}
            {actionType === "archive" && `This will archive ${tenantCount} tenant(s) permanently. Archived tenants CANNOT be restored.`}
            {actionType === "purge" && `This will PERMANENTLY DESTROY ${tenantCount} tenant(s) and all their data.`}
          </DialogDescription>
        </DialogHeader>
        
        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6">
          <div className="space-y-4 py-2">
            {/* Warning message for archive/purge */}
            {warningMessage && (
              <div className={`${colorScheme.bg} border ${colorScheme.border} p-3 rounded-lg`}>
                <p className={`text-sm ${colorScheme.accent} flex items-center gap-2`}>
                  <Warning size={16} />
                  {warningMessage}
                </p>
                {warningDetails && (
                  <ul className="text-sm text-red-200/70 list-disc list-inside space-y-1 mt-2">
                    {warningDetails.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                )}
              </div>
            )}
            
            <div className="bg-zinc-800 p-3 rounded-lg">
              <p className="text-sm text-zinc-300 mb-2">
                Tenants to {actionType}: <span className={`font-semibold ${actionType === "purge" ? "text-red-400" : "text-white"}`}>{tenantCount}</span>
              </p>
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                {tenantNames.map((name, idx) => (
                  <Badge 
                    key={idx} 
                    variant="outline" 
                    className={`text-xs ${actionType === "purge" ? "bg-red-500/10 text-red-300 border-red-500/30" : "bg-zinc-700 text-zinc-100 border-zinc-600"}`}
                  >
                    {name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Sticky footer */}
        <div className="flex-shrink-0 border-t border-zinc-700 px-6 py-4 bg-zinc-900">
          <div className="mb-4">
            <label className="text-sm text-zinc-400 mb-1 block">
              Type {actionType === "purge" ? "exactly: " : ""}<span className={`font-mono ${actionType === "purge" ? "text-red-400 font-bold" : "text-white"}`}>{confirmPhrase}</span> to confirm
            </label>
            <Input
              ref={inputRef}
              defaultValue=""
              onChange={handleInputChange}
              placeholder={actionType === "purge" ? "Type confirmation phrase..." : confirmPhrase}
              className={`bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 ${actionType === "purge" ? "font-mono" : ""}`}
              data-testid={`${actionType}-confirmation-input`}
            />
            {actionType === "purge" && (
              <p className="text-xs text-zinc-500 mt-1">
                This confirmation phrase includes the exact count of tenants you selected.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleExecute}
              disabled={isLoading || !isValid}
              className={`${colorScheme.button} disabled:opacity-50`}
              data-testid={`execute-${actionType}-btn`}
            >
              {isLoading ? "Processing..." : `Execute ${actionLabel}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

// Color schemes for different action types
const ACTION_COLOR_SCHEMES = {
  delete: {
    accent: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    button: "bg-emerald-600 hover:bg-emerald-700",
  },
  archive: {
    accent: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    button: "bg-amber-600 hover:bg-amber-700",
  },
  purge: {
    accent: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    button: "bg-red-600 hover:bg-red-700",
  },
};

// Status badge styling
function getStatusBadge(status) {
  const styles = {
    active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    DELETED: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    DELETING_FAILED: "bg-red-500/20 text-red-400 border-red-500/30",
    ARCHIVED: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    TRUE_PURGED: "bg-red-500/20 text-red-300 border-red-500/30",
  };
  
  const normalizedStatus = status?.toUpperCase() === "PURGED" ? "ARCHIVED" : status;
  return styles[normalizedStatus] || styles.active;
}

export default function AdminTenantLifecycle() {
  const navigate = useNavigate();
  
  // Filter state
  const [filters, setFilters] = useState({
    status: "all",
    inactive_days: "",
    created_before: "",
    limit: 100,
  });
  
  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Selection state
  const [selectedTenants, setSelectedTenants] = useState(new Set());
  
  // Action dialogs
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [purgeDialog, setPurgeDialog] = useState(false);
  const [unblockDialog, setUnblockDialog] = useState(null); // { tenant_id, company_name } or null
  
  // Single source of truth for modal open state (used to freeze background updates)
  const isAnyConfirmModalOpen = deleteDialog || archiveDialog || purgeDialog || unblockDialog;
  const isModalOpenRef = useRef(false);
  
  // Keep ref in sync with state (ref allows synchronous reads in callbacks)
  useEffect(() => {
    isModalOpenRef.current = isAnyConfirmModalOpen;
  }, [isAnyConfirmModalOpen]);
  
  // Operation state
  const [operationLoading, setOperationLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  
  // Jobs tracking state
  const [activeJobs, setActiveJobs] = useState([]); // Jobs being polled
  const pollingRef = useRef(null);
  const pollStartTimeRef = useRef(null);
  
  // Stashed updates to apply when modal closes
  const pendingJobsUpdateRef = useRef(null);
  const pendingPreviewRef = useRef(null);

  // Prevent background scroll when any modal is open
  useEffect(() => {
    if (isAnyConfirmModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      
      // Apply any stashed updates when modal closes
      if (pendingJobsUpdateRef.current) {
        setActiveJobs(pendingJobsUpdateRef.current);
        pendingJobsUpdateRef.current = null;
      }
      if (pendingPreviewRef.current) {
        setPreview(pendingPreviewRef.current);
        pendingPreviewRef.current = null;
      }
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isAnyConfirmModalOpen]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Pause polling when any confirmation modal is open to prevent UI lag
  useEffect(() => {
    if (isAnyConfirmModalOpen && pollingRef.current) {
      // Pause polling while modal is open
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [isAnyConfirmModalOpen]);

  // Poll for job status updates (freezes setState while modal is open)
  const pollJobStatuses = useCallback(async (jobIds, onComplete) => {
    if (!jobIds || jobIds.length === 0) return;
    
    try {
      const response = await fetch(`${API}/admin/tenant-lifecycle/jobs/batch-status`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(jobIds),
      });
      
      if (response.ok) {
        const data = await response.json();
        const jobsMap = {};
        (data.jobs || []).forEach(job => {
          jobsMap[job.job_id] = job;
        });
        
        // Freeze state updates while modal is open - stash for later
        if (isModalOpenRef.current) {
          // Stash the update to apply when modal closes
          pendingJobsUpdateRef.current = (prev => prev.map(job => {
            const updated = jobsMap[job.job_id];
            return updated ? { ...job, ...updated } : job;
          }))(pendingJobsUpdateRef.current || []);
          return; // Don't update state, don't show toasts
        }
        
        setActiveJobs(prev => prev.map(job => {
          const updated = jobsMap[job.job_id];
          return updated ? { ...job, ...updated } : job;
        }));
        
        // Check if all jobs are complete
        const allComplete = (data.jobs || []).every(
          job => job.status === "success" || job.status === "failed"
        );
        
        // Check if we've exceeded max poll duration
        const elapsed = Date.now() - (pollStartTimeRef.current || Date.now());
        if (allComplete || elapsed > MAX_POLL_DURATION) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          
          if (allComplete) {
            const successCount = (data.jobs || []).filter(j => j.status === "success").length;
            const failCount = (data.jobs || []).filter(j => j.status === "failed").length;
            
            if (failCount === 0) {
              toast.success(`All ${successCount} job(s) completed successfully`);
            } else {
              toast.warning(`${successCount} succeeded, ${failCount} failed`);
            }
            if (onComplete) onComplete(); // Callback to refresh preview
          } else {
            toast.info("Some jobs still running - check back later");
          }
        }
      }
    } catch (error) {
      console.error("Poll error:", error);
    }
  }, []);

  // Start polling for active jobs
  const startPolling = useCallback((jobs, onComplete) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    const jobIds = jobs.filter(j => j.job_id).map(j => j.job_id);
    if (jobIds.length === 0) return;
    
    pollStartTimeRef.current = Date.now();
    
    // Poll immediately
    pollJobStatuses(jobIds, onComplete);
    
    // Then poll at interval
    pollingRef.current = setInterval(() => {
      pollJobStatuses(jobIds, onComplete);
    }, POLL_INTERVAL);
  }, [pollJobStatuses]);

  // Resume polling when modal closes if there are incomplete jobs
  // Note: We store runPreview in a ref so it can be used by the resume effect
  const runPreviewRef = useRef(null);
  
  useEffect(() => {
    if (!isAnyConfirmModalOpen && !pollingRef.current) {
      // Check if there are active jobs that need polling
      const incompleteJobs = activeJobs.filter(
        j => j.status !== "success" && j.status !== "failed"
      );
      if (incompleteJobs.length > 0) {
        const jobIds = incompleteJobs.map(j => j.job_id);
        pollStartTimeRef.current = Date.now();
        pollingRef.current = setInterval(() => {
          pollJobStatuses(jobIds, runPreviewRef.current);
        }, POLL_INTERVAL);
        // Poll immediately on resume
        pollJobStatuses(jobIds, runPreviewRef.current);
      }
    }
  }, [isAnyConfirmModalOpen, activeJobs, pollJobStatuses]);

  // Run preview
  const runPreview = useCallback(async () => {
    try {
      setPreviewLoading(true);
      setSelectedTenants(new Set());
      
      const body = {
        status: filters.status,
        limit: parseInt(filters.limit) || 100,
      };
      
      if (filters.inactive_days) {
        body.inactive_days = parseInt(filters.inactive_days);
      }
      if (filters.created_before) {
        body.created_before = filters.created_before;
      }
      
      const response = await fetch(`${API}/admin/tenant-lifecycle/preview`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
        toast.success(`Found ${data.tenants?.length || 0} tenants`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Preview failed");
      }
    } catch (error) {
      console.error("Preview error:", error);
      toast.error("Failed to run preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [filters]);

  // Keep runPreviewRef in sync
  useEffect(() => {
    runPreviewRef.current = runPreview;
  }, [runPreview]);

  // Reconcile stuck transitions (ARCHIVING/DELETING that never completed)
  const reconcileStuckTransitions = useCallback(async () => {
    try {
      setReconcileLoading(true);
      
      const response = await fetch(`${API}/admin/tenant-lifecycle/reconcile`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          older_than_minutes: 2,
          timeout_minutes: 10,
          dry_run: false,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const total = (data.finalized_to_archived || 0) + 
                     (data.finalized_to_deleted || 0) + 
                     (data.marked_archiving_failed || 0) + 
                     (data.marked_deleting_failed || 0);
        
        if (total > 0) {
          toast.success(
            `Reconciled ${data.scanned} tenants: ` +
            `${data.finalized_to_archived} archived, ` +
            `${data.finalized_to_deleted} deleted, ` +
            `${data.marked_archiving_failed + data.marked_deleting_failed} marked failed`
          );
        } else {
          toast.info(`Scanned ${data.scanned} tenants - none needed reconciliation`);
        }
        
        // Refresh preview to show updated statuses
        runPreview();
      } else {
        toast.error(data.detail || "Reconcile failed");
      }
    } catch (error) {
      console.error("Reconcile error:", error);
      toast.error("Failed to reconcile stuck transitions");
    } finally {
      setReconcileLoading(false);
    }
  }, [runPreview]);

  // Toggle tenant selection - uses isSelectableForUI for consistent behavior
  const toggleTenant = (tenantId) => {
    const tenant = preview?.tenants?.find(t => t.tenant_id === tenantId);
    if (!tenant) return;
    
    // Use the shared helper - allows failed tenants to be selected
    if (!isSelectableForUI(tenant)) {
      return; // Don't allow selection of truly non-eligible tenants
    }
    
    setSelectedTenants((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) {
        next.delete(tenantId);
      } else {
        next.add(tenantId);
      }
      return next;
    });
  };

  // Select all eligible for action - uses can_* flags, includes failed tenants for corrective actions
  const selectAllEligible = (action) => {
    if (!preview?.tenants) return;
    
    const eligible = preview.tenants.filter((t) => {
      // Must be selectable for UI (includes failed tenants)
      if (!isSelectableForUI(t)) return false;
      
      // Then check action-specific eligibility
      if (action === "delete") return t.can_delete === true;
      if (action === "archive") return t.can_archive === true;
      if (action === "purge") return t.can_true_purge === true;
      return false;
    });
    
    setSelectedTenants(new Set(eligible.map((t) => t.tenant_id)));
  };

  // Clear selection
  const clearSelection = () => setSelectedTenants(new Set());

  // Get selected tenants by eligibility - uses can_* flags exclusively
  const getSelectedByAction = (action) => {
    if (!preview?.tenants) return [];
    return preview.tenants.filter((t) => {
      if (!selectedTenants.has(t.tenant_id)) return false;
      if (action === "delete") return t.can_delete === true;
      if (action === "archive") return t.can_archive === true;
      if (action === "purge") return t.can_true_purge === true;
      return false;
    });
  };

  // Eligible counts (need these early for confirmation phrases)
  const deleteEligibleCount = getSelectedByAction("delete").length;
  const archiveEligibleCount = getSelectedByAction("archive").length;
  const purgeEligibleCount = getSelectedByAction("purge").length;

  // Generate dynamic purge phrase based on SELECTED count (not preview total)
  const getPurgeConfirmationPhrase = () => {
    const count = purgeEligibleCount;
    // Match backend logic
    return `PURGE ${count} TENANTS`;
  };

  // Execute batch delete
  const executeBatchDelete = useCallback(async () => {
    const eligibleTenants = getSelectedByAction("delete");
    if (eligibleTenants.length === 0) {
      toast.error("No eligible tenants selected for deletion");
      return;
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    
    try {
      setOperationLoading(true);
      
      const response = await fetch(`${API}/admin/tenant-lifecycle/delete`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_ids: eligibleTenants.map((t) => t.tenant_id),
          dry_run_id: preview?.dry_run_id,
          confirmation: "DELETE",
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const data = await response.json();
      
      if (response.ok) {
        setLastResult({ action: "delete", ...data });
        
        // Extract jobs from results
        const jobs = (data.results || [])
          .filter(r => r.success && r.job_id)
          .map(r => ({
            job_id: r.job_id,
            tenant_id: r.tenant_id,
            company_name: r.company_name,
            action: "delete",
            status: "queued",
          }));
        
        // Show immediate failures
        const failures = (data.results || []).filter(r => !r.success);
        if (failures.length > 0) {
          toast.warning(`${failures.length} tenant(s) failed eligibility check`);
        }
        
        if (jobs.length > 0) {
          toast.success(`${jobs.length} delete job(s) queued`);
          setActiveJobs(prev => [...jobs, ...prev]);
          startPolling(jobs, runPreview);
        }
        
        setDeleteDialog(false);
        clearSelection();
      } else {
        toast.error(data.detail || "Delete operation failed");
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        toast.error("Request timed out - operation may still be processing");
      } else {
        console.error("Delete error:", error);
        toast.error("Failed to execute delete operation");
      }
    } finally {
      setOperationLoading(false);
    }
  }, [getSelectedByAction, preview?.dry_run_id, startPolling, runPreview, clearSelection]);

  // Execute batch archive
  const executeBatchArchive = useCallback(async () => {
    const eligibleTenants = getSelectedByAction("archive");
    if (eligibleTenants.length === 0) {
      toast.error("No eligible tenants selected for archiving");
      return;
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    
    try {
      setOperationLoading(true);
      
      const response = await fetch(`${API}/admin/tenant-lifecycle/archive`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_ids: eligibleTenants.map((t) => t.tenant_id),
          dry_run_id: preview?.dry_run_id,
          confirmation: "ARCHIVE",
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const data = await response.json();
      
      if (response.ok) {
        setLastResult({ action: "archive", ...data });
        
        // Extract jobs from results
        const jobs = (data.results || [])
          .filter(r => r.success && r.job_id)
          .map(r => ({
            job_id: r.job_id,
            tenant_id: r.tenant_id,
            company_name: r.company_name,
            action: "archive",
            status: "queued",
          }));
        
        // Show immediate failures
        const failures = (data.results || []).filter(r => !r.success);
        if (failures.length > 0) {
          toast.warning(`${failures.length} tenant(s) failed eligibility check`);
        }
        
        if (jobs.length > 0) {
          toast.success(`${jobs.length} archive job(s) queued`);
          setActiveJobs(prev => [...jobs, ...prev]);
          startPolling(jobs, runPreview);
        }
        
        setArchiveDialog(false);
        clearSelection();
      } else {
        toast.error(data.detail || "Archive operation failed");
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        toast.error("Request timed out - operation may still be processing");
      } else {
        console.error("Archive error:", error);
        toast.error("Failed to execute archive operation");
      }
    } finally {
      setOperationLoading(false);
    }
  }, [getSelectedByAction, preview?.dry_run_id, startPolling, runPreview, clearSelection]);

  // Execute batch purge
  const executeBatchPurge = useCallback(async () => {
    const eligibleTenants = getSelectedByAction("purge");
    const expectedPhrase = getPurgeConfirmationPhrase();
    
    if (eligibleTenants.length === 0) {
      toast.error("No eligible tenants selected for purging");
      return;
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    
    try {
      setOperationLoading(true);
      
      const response = await fetch(`${API}/admin/tenant-lifecycle/true-purge`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_ids: eligibleTenants.map((t) => t.tenant_id),
          dry_run_id: preview?.dry_run_id,
          snapshot_before_purge: true,
          confirmation_phrase: expectedPhrase,
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const data = await response.json();
      
      if (response.ok) {
        setLastResult({ action: "purge", ...data });
        
        // Extract jobs from results
        const jobs = (data.results || [])
          .filter(r => r.success && r.job_id)
          .map(r => ({
            job_id: r.job_id,
            tenant_id: r.tenant_id,
            company_name: r.company_name,
            action: "true_purge",
            status: "queued",
          }));
        
        // Show immediate failures
        const failures = (data.results || []).filter(r => !r.success);
        if (failures.length > 0) {
          toast.warning(`${failures.length} tenant(s) failed eligibility check`);
        }
        
        if (jobs.length > 0) {
          toast.success(`${jobs.length} purge job(s) queued`);
          setActiveJobs(prev => [...jobs, ...prev]);
          startPolling(jobs, runPreview);
        }
        
        setPurgeDialog(false);
        clearSelection();
      } else {
        toast.error(data.detail || "Purge operation failed");
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        toast.error("Request timed out - operation may still be processing");
      } else {
        console.error("Purge error:", error);
        toast.error("Failed to execute purge operation");
      }
    } finally {
      setOperationLoading(false);
    }
  }, [getSelectedByAction, getPurgeConfirmationPhrase, preview?.dry_run_id, startPolling, runPreview, clearSelection]);

  // Handle unblock action for BLOCKED + *_FAILED tenants
  const handleUnblock = useCallback(async (tenantId) => {
    if (!tenantId) return;
    
    setOperationLoading(true);
    try {
      const response = await fetch(`${API}/admin/tenant-lifecycle/unblock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ tenant_ids: [tenantId] }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Unblock failed");
      }
      
      const result = await response.json();
      
      if (result.updated > 0) {
        toast.success(`Tenant unblocked and reverted to ARCHIVED`);
        // Refresh preview to show updated state
        runPreview();
      } else {
        const skipReason = result.results?.[0]?.reason || "unknown";
        toast.info(`Unblock skipped: ${skipReason}`);
      }
    } catch (error) {
      console.error("Unblock error:", error);
      toast.error(error.message || "Failed to unblock tenant");
    } finally {
      setOperationLoading(false);
      setUnblockDialog(null);
    }
  }, [runPreview]);

  // Check if actions are available (preview must exist)
  const actionsEnabled = preview !== null && preview.dry_run_id;

  return (
    <div className="p-6 space-y-6" data-testid="tenant-lifecycle-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/tenants")}
          className="text-zinc-400 hover:text-white"
        >
          <ArrowLeft size={18} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Buildings size={28} className="text-blue-400" />
            Tenant Lifecycle Orchestrator
          </h1>
          <p className="text-zinc-500 mt-1">
            Batch preview, delete, archive, and purge tenants
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Funnel size={18} className="text-zinc-400" />
            Preview Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Status</label>
              <Select
                value={filters.status}
                onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="deleted">Deleted Only</SelectItem>
                  <SelectItem value="archived">Archived Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Inactive Days</label>
              <Input
                type="number"
                placeholder="e.g. 90"
                value={filters.inactive_days}
                onChange={(e) => setFilters((f) => ({ ...f, inactive_days: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Created Before</label>
              <Input
                type="date"
                value={filters.created_before}
                onChange={(e) => setFilters((f) => ({ ...f, created_before: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 [color-scheme:dark]"
              />
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Limit</label>
              <Input
                type="number"
                value={filters.limit}
                onChange={(e) => setFilters((f) => ({ ...f, limit: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={runPreview}
              disabled={previewLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {previewLoading ? (
                <ArrowsClockwise size={16} className="animate-spin mr-2" />
              ) : (
                <Eye size={16} className="mr-2" />
              )}
              Run Preview
            </Button>
            
            <Button
              onClick={reconcileStuckTransitions}
              disabled={reconcileLoading}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              data-testid="reconcile-btn"
            >
              {reconcileLoading ? (
                <ArrowsClockwise size={16} className="animate-spin mr-2" />
              ) : (
                <ArrowsClockwise size={16} className="mr-2" />
              )}
              Reconcile Stuck
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Results - Hide heavy content while modal is open to reduce repaint cost */}
      {preview && (
        <div style={{ visibility: isAnyConfirmModalOpen ? "hidden" : "visible" }}>
          {/* Summary */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="pt-6">
              <div className="grid grid-cols-5 gap-4 text-center">
                <div className="bg-zinc-800 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-white">{preview.summary?.total_tenants || 0}</div>
                  <div className="text-xs text-zinc-500">Total Found</div>
                </div>
                <div className="bg-zinc-800 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-400">{preview.summary?.can_delete_count || 0}</div>
                  <div className="text-xs text-zinc-500">Can Delete</div>
                </div>
                <div className="bg-zinc-800 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-amber-400">{preview.summary?.can_archive_count || 0}</div>
                  <div className="text-xs text-zinc-500">Can Archive</div>
                </div>
                <div className="bg-zinc-800 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-400">{preview.summary?.can_true_purge_count || 0}</div>
                  <div className="text-xs text-zinc-500">Can Purge</div>
                </div>
                <div className="bg-zinc-800 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-zinc-300">{preview.summary?.total_documents || 0}</div>
                  <div className="text-xs text-zinc-500">Total Documents</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <div className="text-xs text-zinc-500">
                  Preview ID: <span className="font-mono">{preview.dry_run_id}</span>
                </div>
                <div className="text-xs text-zinc-400 flex items-center gap-1">
                  <CheckCircle size={12} className="text-emerald-400" />
                  Preview loaded - select tenants and choose an action
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selection Actions */}
          <div className="flex items-center justify-between gap-4 bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-400">
                {selectedTenants.size} selected
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllEligible("delete")}
                disabled={!preview.summary?.can_delete_count}
                className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 disabled:opacity-50"
              >
                Select Deletable ({preview.summary?.can_delete_count || 0})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllEligible("archive")}
                disabled={!preview.summary?.can_archive_count}
                className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10 disabled:opacity-50"
              >
                Select Archivable ({preview.summary?.can_archive_count || 0})
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectAllEligible("purge")}
                disabled={!preview.summary?.can_true_purge_count}
                className="text-red-400 border-red-500/30 hover:bg-red-500/10 disabled:opacity-50"
              >
                Select Purgeable ({preview.summary?.can_true_purge_count || 0})
              </Button>
              {selectedTenants.size > 0 && (
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setDeleteDialog(true)}
                disabled={deleteEligibleCount === 0}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                data-testid="batch-delete-btn"
              >
                <Trash size={16} className="mr-2" />
                Delete ({deleteEligibleCount})
              </Button>
              <Button
                onClick={() => setArchiveDialog(true)}
                disabled={archiveEligibleCount === 0}
                className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                data-testid="batch-archive-btn"
              >
                <Archive size={16} className="mr-2" />
                Archive ({archiveEligibleCount})
              </Button>
              <Button
                onClick={() => setPurgeDialog(true)}
                disabled={purgeEligibleCount === 0}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                data-testid="batch-purge-btn"
              >
                <Fire size={16} className="mr-2" />
                Purge ({purgeEligibleCount})
              </Button>
            </div>
          </div>

          {/* Tenant List */}
          <div className="space-y-2">
            {preview.tenants?.length === 0 && (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-8 text-center text-zinc-500">
                  No tenants match the current filters
                </CardContent>
              </Card>
            )}
            {preview.tenants?.map((tenant) => {
              const isSelected = selectedTenants.has(tenant.tenant_id);
              
              // Use shared helpers for consistent behavior
              const failed = isFailedTenant(tenant);
              const selectableForUI = isSelectableForUI(tenant);
              
              // Status display
              const statusDisplay = String(getTenantStatusValue(tenant)).toUpperCase() || "ACTIVE";
              const isTerminal = tenant.is_terminal || tenant.normalized_status === "true_purged";
              
              // Row-level wrapper classes for pointer-events control
              const rowWrapperClass = failed
                ? "pointer-events-auto opacity-100"
                : !selectableForUI
                  ? "pointer-events-none opacity-60"
                  : "pointer-events-auto";
              
              return (
                <div
                  key={tenant.tenant_id}
                  className={rowWrapperClass}
                >
                  <Card
                    data-testid={`tenant-card-${tenant.tenant_id}`}
                    className={`bg-zinc-900 border-zinc-800 transition-colors ${
                      failed
                        ? "border-red-500/50 bg-red-500/5 cursor-pointer"
                        : !selectableForUI 
                          ? "cursor-not-allowed" 
                          : isSelected 
                            ? "border-blue-500 bg-blue-500/5 cursor-pointer" 
                            : "hover:border-zinc-700 cursor-pointer"
                    }`}
                    onClick={() => selectableForUI && toggleTenant(tenant.tenant_id)}
                  >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => selectableForUI && toggleTenant(tenant.tenant_id)}
                        disabled={!selectableForUI}
                        className={`data-[state=checked]:bg-blue-600 ${!selectableForUI ? "opacity-50" : ""}`}
                        data-testid={`tenant-checkbox-${tenant.tenant_id}`}
                      />
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className={`font-semibold ${failed ? "text-red-300" : !selectableForUI ? "text-zinc-500" : "text-white"}`}>
                            {tenant.company_name || "Unnamed"}
                          </span>
                          <Badge
                            variant="outline"
                            className={failed 
                              ? "bg-red-500/20 text-red-400 border-red-500/50 font-semibold"
                              : getStatusBadge(tenant.status)}
                          >
                            {statusDisplay}
                          </Badge>
                          
                          {/* Eligibility badges */}
                          {tenant.can_delete && (
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                              Can Delete
                            </Badge>
                          )}
                          {tenant.can_archive && (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-xs">
                              Can Archive
                            </Badge>
                          )}
                          {tenant.can_true_purge && (
                            <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
                              Can Purge
                            </Badge>
                          )}
                          {isTerminal && (
                            <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/30 text-xs">
                              Terminal (No Actions)
                            </Badge>
                          )}
                          {tenant.is_transitional && !failed && (
                            <Badge variant="outline" className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30 text-xs">
                              In Progress
                            </Badge>
                          )}

                          {failed && (
                            <Badge variant="outline" className="bg-red-500/20 text-red-300 border-red-500/40 text-xs font-semibold">
                              BLOCKED - Admin Action Required
                            </Badge>
                          )}
                          {/* Unblock button - only for BLOCKED + *_FAILED tenants */}
                          {failed && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 px-2 text-xs bg-orange-500/10 text-orange-400 border-orange-500/30 hover:bg-orange-500/20"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUnblockDialog({ 
                                  tenant_id: tenant.tenant_id, 
                                  company_name: tenant.company_name 
                                });
                              }}
                              disabled={operationLoading}
                              data-testid={`unblock-btn-${tenant.tenant_id}`}
                            >
                              Unblock (Revert to Archived)
                            </Button>
                          )}
                          {tenant.has_export && (
                            <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs">
                              Has Export
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-zinc-500">
                          <span className="font-mono text-xs">{tenant.tenant_id}</span>
                          {tenant.created_at && (
                            <span>Created: {new Date(tenant.created_at).toLocaleDateString()}</span>
                          )}
                          {tenant.last_activity_at && (
                            <span>Last active: {new Date(tenant.last_activity_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Counts */}
                      <div className="flex items-center gap-6 text-center">
                        <div>
                          <div className="text-lg font-semibold text-white">{tenant.counts?.customers || 0}</div>
                          <div className="text-xs text-zinc-500">Customers</div>
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-white">{tenant.counts?.jobs || 0}</div>
                          <div className="text-xs text-zinc-500">Jobs</div>
                        </div>
                        <div>
                          <div className="text-lg font-semibold text-white">{tenant.counts?.total || 0}</div>
                          <div className="text-xs text-zinc-500">Total Docs</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Preview State */}
      {!preview && !previewLoading && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-12 text-center">
            <Eye size={48} className="mx-auto text-zinc-600 mb-4" />
            <h3 className="text-lg font-semibold text-zinc-300 mb-2">Run Preview First</h3>
            <p className="text-zinc-500 max-w-md mx-auto">
              Configure filters above and click &quot;Run Preview&quot; to see eligible tenants.
              All lifecycle actions require a preview to ensure you&apos;re operating on the intended set.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmActionModal
        isOpen={deleteDialog}
        onOpenChange={setDeleteDialog}
        actionType="delete"
        actionLabel="Delete"
        confirmPhrase="DELETE"
        tenantNames={getSelectedByAction("delete").map(t => t.company_name || t.tenant_id)}
        tenantCount={deleteEligibleCount}
        warningMessage={null}
        warningDetails={null}
        colorScheme={ACTION_COLOR_SCHEMES.delete}
        icon={Trash}
        onExecute={executeBatchDelete}
        isLoading={operationLoading}
      />

      {/* Archive Confirmation Modal */}
      <ConfirmActionModal
        isOpen={archiveDialog}
        onOpenChange={setArchiveDialog}
        actionType="archive"
        actionLabel="Archive"
        confirmPhrase="ARCHIVE"
        tenantNames={getSelectedByAction("archive").map(t => t.company_name || t.tenant_id)}
        tenantCount={archiveEligibleCount}
        warningMessage="This action cannot be undone. Exports are retained."
        warningDetails={null}
        colorScheme={ACTION_COLOR_SCHEMES.archive}
        icon={Archive}
        onExecute={executeBatchArchive}
        isLoading={operationLoading}
      />

      {/* Purge Confirmation Modal */}
      <ConfirmActionModal
        isOpen={purgeDialog}
        onOpenChange={setPurgeDialog}
        actionType="purge"
        actionLabel="Purge"
        confirmPhrase={getPurgeConfirmationPhrase()}
        tenantNames={getSelectedByAction("purge").map(t => `${t.company_name || t.tenant_id} (${t.counts?.total || 0} docs)`)}
        tenantCount={purgeEligibleCount}
        warningMessage="PERMANENT DATA DESTRUCTION"
        warningDetails={[
          "All database records will be deleted",
          "All photos and files in R2 storage will be destroyed",
          "All exports and backups will be removed",
          "Tenant metadata will be marked as TRUE_PURGED"
        ]}
        colorScheme={ACTION_COLOR_SCHEMES.purge}
        icon={Fire}
        onExecute={executeBatchPurge}
        isLoading={operationLoading}
      />

      {/* Unblock Confirmation Dialog - Simple one-line confirm */}
      <Dialog open={!!unblockDialog} onOpenChange={(open) => !open && setUnblockDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-orange-400 flex items-center gap-2">
              <Warning size={20} />
              Unblock Tenant
            </DialogTitle>
            <DialogDescription className="text-zinc-300 pt-2">
              Revert this tenant to ARCHIVED and clear the BLOCKED state?
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-zinc-400 py-2">
            <span className="font-mono">{unblockDialog?.tenant_id}</span>
            {unblockDialog?.company_name && (
              <span className="ml-2">({unblockDialog.company_name})</span>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setUnblockDialog(null)}
              disabled={operationLoading}
              className="border-zinc-700 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleUnblock(unblockDialog?.tenant_id)}
              disabled={operationLoading}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="unblock-confirm-btn"
            >
              {operationLoading ? <Spinner className="animate-spin mr-2" size={16} /> : null}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Jobs Panel */}
      {activeJobs.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800" data-testid="jobs-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowsClockwise size={18} className={pollingRef.current ? "animate-spin text-blue-400" : "text-zinc-400"} />
              Active Jobs
              {pollingRef.current && (
                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30 ml-2">
                  Polling
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {activeJobs.map((job) => (
                <div 
                  key={job.job_id}
                  className={`p-3 rounded-lg border ${
                    job.status === "success" 
                      ? "bg-emerald-500/10 border-emerald-500/30" 
                      : job.status === "failed"
                        ? "bg-red-500/10 border-red-500/30"
                        : job.status === "running"
                          ? "bg-blue-500/10 border-blue-500/30"
                          : "bg-zinc-800 border-zinc-700"
                  }`}
                  data-testid={`job-${job.job_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Status icon */}
                      {job.status === "queued" && <Clock size={16} className="text-zinc-400" />}
                      {job.status === "running" && <Spinner size={16} className="text-blue-400 animate-spin" />}
                      {job.status === "success" && <Check size={16} className="text-emerald-400" />}
                      {job.status === "failed" && <X size={16} className="text-red-400" />}
                      
                      <div>
                        <div className="text-sm font-medium text-white">
                          {job.company_name || job.tenant_id}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {job.action?.replace("_", " ").toUpperCase()} • {job.job_id}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          job.status === "success" 
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                            : job.status === "failed"
                              ? "bg-red-500/10 text-red-400 border-red-500/30"
                              : job.status === "running"
                                ? "bg-blue-500/10 text-blue-400 border-blue-500/30"
                                : "bg-zinc-700 text-zinc-300 border-zinc-600"
                        }`}
                      >
                        {job.status}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* ALWAYS VISIBLE ERROR DETAILS FOR FAILED JOBS */}
                  {job.status === "failed" && (
                    <div className="mt-3 pt-3 border-t border-red-500/20 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-zinc-500">Tenant ID:</span>
                        <code className="text-xs text-red-300 bg-red-500/10 px-1.5 py-0.5 rounded font-mono">
                          {job.tenant_id}
                        </code>
                      </div>
                      {job.error_code && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-zinc-500">Error Code:</span>
                          <Badge variant="outline" className="text-xs bg-red-500/20 text-red-300 border-red-500/40 font-mono">
                            {job.error_code}
                          </Badge>
                        </div>
                      )}
                      {job.error && (
                        <div className="space-y-1">
                          <span className="text-xs text-zinc-500">Error Message:</span>
                          <div className="text-xs text-red-300 bg-red-500/10 p-2 rounded font-mono break-all">
                            {job.error}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Clear completed jobs */}
            {activeJobs.some(j => j.status === "success" || j.status === "failed") && (
              <div className="mt-3 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveJobs(prev => prev.filter(j => j.status !== "success" && j.status !== "failed"))}
                  className="text-zinc-400 hover:text-white text-xs"
                >
                  Clear Completed
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Last Result */}
      {lastResult && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {lastResult.summary?.failed === 0 ? (
                <CheckCircle size={18} className="text-emerald-400" />
              ) : (
                <XCircle size={18} className="text-red-400" />
              )}
              Last Operation Result
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div className="bg-zinc-800 p-3 rounded-lg">
                <div className="text-lg font-bold">{lastResult.action?.toUpperCase()}</div>
                <div className="text-xs text-zinc-500">Action</div>
              </div>
              <div className="bg-zinc-800 p-3 rounded-lg">
                <div className="text-lg font-bold">{lastResult.summary?.requested || 0}</div>
                <div className="text-xs text-zinc-500">Requested</div>
              </div>
              <div className="bg-zinc-800 p-3 rounded-lg">
                <div className="text-lg font-bold text-blue-400">{lastResult.summary?.queued || 0}</div>
                <div className="text-xs text-zinc-500">Queued</div>
              </div>
              <div className="bg-zinc-800 p-3 rounded-lg">
                <div className="text-lg font-bold text-red-400">{lastResult.summary?.failed || 0}</div>
                <div className="text-xs text-zinc-500">Rejected</div>
              </div>
            </div>
            
            {lastResult.results?.some((r) => !r.success) && (
              <div className="mt-4 space-y-2">
                <p className="text-sm text-zinc-400">Failed operations:</p>
                {lastResult.results?.filter((r) => !r.success).map((r) => (
                  <div key={r.tenant_id} className="bg-red-500/10 border border-red-500/30 p-3 rounded text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-zinc-300">{r.tenant_id}</span>
                      {r.error_code && (
                        <Badge variant="outline" className="text-xs bg-red-500/10 text-red-400 border-red-500/30">
                          {r.error_code}
                        </Badge>
                      )}
                    </div>
                    <p className="text-zinc-400 mt-1">{r.message || r.error || "Unknown error"}</p>
                  </div>
                ))}
              </div>
            )}
            
            <div className="text-xs text-zinc-500 mt-4">
              Operation ID: <span className="font-mono">{lastResult.operation_id}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
